import { conversationRequestScope } from './requestScope';

// Anchor the maximum delay to this burst, independent of the previous account
// or a wall-clock adjustment. At most two timers and one argument tuple exist.
export function coalesceConversationStats(dispatch) {
  let quiet = null;
  let deadline = null;
  let latest = null;
  let flight = null;
  let deferredForFlight = false;
  const invoke = () => {
    if (quiet !== null) clearTimeout(quiet);
    if (deadline !== null) clearTimeout(deadline);
    quiet = null; deadline = null;
    if (!latest) return;
    // Serialize a healthy same-query request. A new account/filter invalidates
    // its scope and may start independently; the obsolete result cannot commit.
    if (flight?.current()) { deferredForFlight = true; return; }
    const args = latest; latest = null; deferredForFlight = false;
    const active = { current: args[2] };
    flight = active;
    const settled = () => {
      if (flight !== active) return;
      flight = null;
      if (latest && deferredForFlight) invoke();
    };
    Promise.resolve(dispatch(...args)).then(settled, settled);
  };
  return (...args) => {
    latest = args;
    if (quiet !== null) clearTimeout(quiet);
    quiet = setTimeout(invoke, 500);
    if (deadline === null) deadline = setTimeout(invoke, 2000);
  };
}

// Native list actions already fence their synchronous metadata delivery.
// Fence the separately debounced metadata request at scheduling time too.
export function scopedConversationStats(originalActions, originalGetters) {
  let generation = 0;
  let dispatchVersion = 0;
  let selection = null;
  const scopes = new WeakMap();
  const wrap = (action, context, payload, listDelivery = false) => {
    const currentView = conversationRequestScope(context);
    if (!currentView()) return;
    const query = listDelivery ? null : JSON.stringify(Object.fromEntries(Object.entries(payload || {}).sort(([a], [b]) => a.localeCompare(b))));
    const scope = { route: context.rootState.route, account: context.rootGetters.getCurrentAccountId, actor: context.rootGetters.getCurrentUser.id, query };
    if (listDelivery || !selection || Object.keys(scope).some(key => scope[key] !== selection[key])) {
      generation++; selection = listDelivery ? null : scope;
    }
    const ticket = generation;
    const current = () => currentView() && ticket === generation;
    if (!current()) return;
    return action({
      ...context,
      statsRequestCurrent: current,
      statsRequestDispatch: () => {
        if (!current()) return () => false;
        const version = ++dispatchVersion;
        return () => current() && version === dispatchVersion;
      },
      commit: (...values) => {
        if (!current()) return;
        const previous = scopes.get(context.state);
        scopes.set(context.state, currentView);
        try { context.commit(...values); }
        catch (error) {
          if (previous) scopes.set(context.state, previous);
          else scopes.delete(context.state);
          throw error;
        }
      },
    }, payload);
  };
  return {
    actions: {
      ...originalActions,
      get: (context, params) => wrap(originalActions.get, context, params),
      // Fresh list metadata supersedes a queued or in-flight /meta response.
      set: (context, meta) => wrap(originalActions.set, context, meta, true),
    },
    getters: {
      ...originalGetters,
      getStats: (state, getters, rootState, rootGetters) => {
        if (rootState && rootGetters && (!state.attentionScopeRevision || !scopes.get(state)?.())) {
          return { mineCount: null, unAssignedCount: null, allCount: null };
        }
        return originalGetters.getStats(state, getters, rootState, rootGetters);
      },
    },
  };
}
